import i18next from 'i18next'
import React from 'react'
import { InView } from 'react-intersection-observer'
import { Link } from 'widgets-for-react'
import { ButtonGhost, CreateCollectionButton } from '../../../../utils/buttons'
import { DataLayer } from '../../../../utils/datalayer'
import { blockInviewHandler } from '../../../../utils/inview'
import { SVGIcon } from '../../../../utils/svgicon'
import { ImageData } from '../../image/imageState'
import { RootLayout } from '../../rootLayout'
import { routeUpdaters } from '../../routes/routesState'
import { CollectionsData } from '../collectionsLoader/collectionsLoaderState'

export type CollectionTeaserProps = {
  path: string
  title: string
  numberOfComponents: number
  firstSixComponentImages: [
    ImageData & { isOwnComponent?: boolean },
    ImageData & { isOwnComponent?: boolean },
    ImageData & { isOwnComponent?: boolean },
    ImageData & { isOwnComponent?: boolean },
    ImageData & { isOwnComponent?: boolean },
    ImageData & { isOwnComponent?: boolean }
  ]
}

const imagePositions = ['tr', 'cr', 'br', 'bl', 'cl', 'tl']
const imagePlaceholder = (
  <svg width="180px" height="180px" aria-hidden="true">
    <use xlinkHref="#icon--shape-placeholder" />
  </svg>
)

export const CollectionTeaserLayout = (p: CollectionTeaserProps) => (
  <li className="teaser--collection block__item">
    <article className="teaser__inner">
      <Link
        to={p.path}
        className="teaser__link"
        onClick={() => DataLayer.goToCollection('dashboard')}
      >
        <div className="teaser__content">
          <h3 className="teaser__title title--xs">
            <span>{p.title}</span>
          </h3>
          <p className="teaser__subtitle text--s">{`${
            p.numberOfComponents
          } ${i18next
            .t('component:componentsTitle', { count: p.numberOfComponents })
            ?.toLowerCase()}`}</p>
        </div>
        <div className="teaser__visual">
          <div className="hex">
            {p.firstSixComponentImages.map((image, i) => (
              <div
                className={`hex__item hex__item--${imagePositions[i]} ${
                  image.isOwnComponent ? 'hex__item--own' : ''
                }`}
                key={i}
              >
                <svg
                  className="hex__svg"
                  xmlns="http://www.w3.org/2000/svg"
                  width="135px"
                  height="156px"
                  viewBox="0 0 135 156"
                >
                  <g clipPath={`url(#hex-${imagePositions[i]})`}>
                    {image.Url ? (
                      <image
                        className="hex__image"
                        width="135px"
                        height="156px"
                        xlinkHref={image.Url}
                      />
                    ) : (
                      imagePlaceholder
                    )}
                  </g>
                </svg>
              </div>
            ))}
          </div>
        </div>
      </Link>
    </article>
  </li>
)

export type CollectionTeasersLayoutProps = {
  title: string
  intro?: string
  collections: CollectionsData | 'loading' | 'error'
  showReadMoreLink?: boolean
} & (
  | { kind: 'home-logged-in'; onCreate: () => void; }
  | { kind: 'dashboard'; onCreate: () => void; paywalled: boolean }
  | { kind: 'search-results' }
)

export const CollectionTeasersLayout = (p: CollectionTeasersLayoutProps) => {
  const hasContainer = p.kind === 'dashboard' || p.kind === 'home-logged-in'
  const isSearchResult = p.kind === 'search-results'
  return (
    <InView
      as="section"
      threshold={0}
      triggerOnce={true}
      onChange={(isInView, entry) => blockInviewHandler(isInView, entry.target)}
      className={`block block--collection-teasers collection-teasers--${
        p.kind
      }${hasContainer ? ' container--m gutter--m' : ''}${
        p.showReadMoreLink ? ' has--link' : ''
      } cf`}
    >
      <div className="block__inner">
        <header
          className={`block__header${
            isSearchResult ? ' block__header--search' : ''
          }`}
        >
          <h2
            className={`block__title ${
              p.kind === 'home-logged-in' ? 'title--xs' : 'title--s'
            }`}
            dangerouslySetInnerHTML={{ __html: p.title }}
          />
          {p.intro && (
            <p className="block__intro block__intro--840">{p.intro}</p>
          )}

          {p.kind == 'dashboard' && !p.paywalled && (
            <div className="block__clickables">
              <div className="block__clickable">
                <CreateCollectionButton onClick={p.onCreate} />
              </div>
            </div>
          )}
        </header>

        {!(p.kind == 'dashboard' && p.paywalled) && (
          <div className="block__content">
            {p.collections == 'loading' ? (
              <RootLayout.Loader hasWrapper={true} showText={true} />
            ) : p.collections == 'error' ? (
              <RootLayout.LoadingFailed
                text={i18next.t('common:loadingFailed', {
                  entity: i18next
                    .t('collection:collectionsTitle')
                    .toLowerCase(),
                })}
              />
            ) : p.collections.length > 0 ? (
              <>
                <ul className="block__list">
                  {p.collections.map((collection) => {
                    const imageOrPlaceholder = (
                      i: number
                    ): ImageData & { isOwnComponent?: boolean } =>
                      collection.Components.length > i
                        ? collection.Components[i].IsUserComponent
                          ? { Title: '', Url: '', isOwnComponent: true } // Triggers placeholder svg
                          : { ...collection.Components[i].ImageURL }
                        : { Title: '', Url: '' } // Triggers placeholder svg
                    return (
                      <CollectionTeaserLayout
                        key={collection.Id}
                        path={`${
                          i18next.language == 'en'
                            ? `/en/dashboard/collection/${collection.Id}`
                            : `/nl/dashboard/collectie/${collection.Id}`
                        }`}
                        title={collection.Name}
                        numberOfComponents={collection.Components.length}
                        firstSixComponentImages={[
                          imageOrPlaceholder(0),
                          imageOrPlaceholder(1),
                          imageOrPlaceholder(2),
                          imageOrPlaceholder(3),
                          imageOrPlaceholder(4),
                          imageOrPlaceholder(5),
                        ]}
                      />
                    )
                  })}
                </ul>
              </>
            ) : p.kind == 'home-logged-in' ? (
              <HomepageEmptyState onCreate={p.onCreate} />
            ) : (
              <RootLayout.EmptyState
                text={i18next.t('common:emptyState', {
                  entity: i18next
                    .t('collection:collectionsTitle')
                    .toLowerCase(),
                })}
              />
            )}
          </div>
        )}

        {p.showReadMoreLink && (
          <ButtonGhost
            kind="link"
            to={routeUpdaters(i18next.language).dashboard.url}
            size="s"
            label={i18next.t('common:more')}
            iconId="chevron-right"
            iconSize="10"
            extraClasses="block__button"
          />
        )}
      </div>
    </InView>
  )
}

export const HomepageEmptyState = (p: { onCreate: () => void }) => (
  <div className="block__empty-collections">
    <div className="block__empty-collections-inner container--s">
      <SVGIcon iconId="hexagon-sliced-open" size={64} />

      <h3 className="title--xs">
        {i18next.t('home:collectionsEmptyStateTitle')}
      </h3>

      <p className="text--s">{i18next.t('home:collectionsEmptyStateIntro')}</p>

      <CreateCollectionButton onClick={p.onCreate} />
    </div>
  </div>
)
